import { BaseActionWidget, DeleteOneActionWidget, ModelDefaultActionName, SPI, Widget } from '@kunlun/dependencies';
import { Modal } from 'ant-design-vue';

interface ActivityNavRecord {
  sideNavCode?: string;
}

interface ActivityItemNavRelRecord {
  sideNavCode?: string;
  sideNavName?: string;
  horizontalNavCode?: string;
  horizontalNavName?: string;
}

interface ActivityItemRecord {
  sideNavCodes?: string;
  navDisplayText?: string;
  navRelList?: ActivityItemNavRelRecord[];
}

@SPI.ClassFactory(
  BaseActionWidget.Token({
    model: 'deli.aries.settlement.DeliAriesCmNavProxy',
    name: ModelDefaultActionName.$$internal_DeleteOne,
    inline: true,
    widget: 'ClientAction'
  })
)
export class DeleteActivityNavAction extends DeleteOneActionWidget {
  @Widget.Reactive()
  protected get formData() {
    return this.rootData?.[0] ?? {};
  }

  protected async clickAction() {
    const deletedNavCodes = this.getDeletedNavCodes();
    if (!deletedNavCodes.size) {
      return super.clickAction();
    }

    const affectedItemCount = this.getAffectedItemCount(deletedNavCodes);
    if (!affectedItemCount) {
      return super.clickAction();
    }

    return new Promise((resolve) => {
      Modal.confirm({
        title: '提示',
        content: `勾选的侧边导航已绑定${affectedItemCount}个商品，删除后会把商品绑定的导航一起删除`,
        okText: '确定',
        cancelText: '取消',
        class: 'oio-modal',
        onOk: async () => {
          this.clearItemNavBindings(deletedNavCodes);
          const result = await super.clickAction();
          this.refreshCallChaining?.syncCall(true);
          resolve(result);
        },
        onCancel: () => resolve(false)
      });
    }) as any;
  }

  private getDeletedNavCodes() {
    const deletedNavs = (this.activeRecords || []) as ActivityNavRecord[];
    return new Set(deletedNavs.map((item) => item.sideNavCode).filter(Boolean) as string[]);
  }

  private getActivityItems() {
    return (this.formData.activityItemList || []) as ActivityItemRecord[];
  }

  private getAffectedItemCount(deletedNavCodes: Set<string>) {
    return this.getActivityItems().filter((item) => this.hasDeletedNavBinding(item, deletedNavCodes)).length;
  }

  private hasDeletedNavBinding(item: ActivityItemRecord, deletedNavCodes: Set<string>) {
    const navRelList = item.navRelList || [];
    if (navRelList.some((rel) => rel.sideNavCode && deletedNavCodes.has(rel.sideNavCode))) {
      return true;
    }

    const sideNavCodes = (item.sideNavCodes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
    return sideNavCodes.some((code) => deletedNavCodes.has(code));
  }

  private clearItemNavBindings(deletedNavCodes: Set<string>) {
    this.getActivityItems().forEach((item) => {
      if (!this.hasDeletedNavBinding(item, deletedNavCodes)) {
        return;
      }

      const nextNavRelList = (item.navRelList || []).filter((rel) => !rel.sideNavCode || !deletedNavCodes.has(rel.sideNavCode));
      const fallbackSideNavCodes = (item.sideNavCodes || '')
        .split(',')
        .map((code) => code.trim())
        .filter((code) => code && !deletedNavCodes.has(code));
      const nextSideNavCodes = Array.from(new Set([...(nextNavRelList.map((rel) => rel.sideNavCode).filter(Boolean) as string[]), ...fallbackSideNavCodes]));
      const nextNavDisplayText = nextNavRelList.map((rel) => this.buildNavDisplayText(rel)).filter(Boolean);

      item.navRelList = nextNavRelList.length ? nextNavRelList : undefined;
      item.sideNavCodes = nextSideNavCodes.length ? nextSideNavCodes.join(',') : undefined;
      item.navDisplayText = nextNavDisplayText.length ? nextNavDisplayText.join(',') : undefined;
    });
  }

  private buildNavDisplayText(navRel: ActivityItemNavRelRecord) {
    if (!navRel.sideNavCode) {
      return '';
    }
    return `${navRel.sideNavName || ''}(${navRel.sideNavCode})`;
  }
}
